import type { PlanDetailsCopy } from '../planDetails'

const es: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'Tu edad',
    retirement_age: 'Cuándo te jubilas',
    annual_income: 'Ingresos anuales',
    annual_savings: 'Ahorro anual',
    inflation: 'Inflación',
    realization_tax_rate: 'Tipo del impuesto sobre plusvalías',
    cost_basis: 'Coste de adquisición',
    w0: 'Patrimonio invertible actual',
    primary_residence: 'Tu vivienda',
    total_investable: 'Patrimonio invertible',
    expected_return: 'Rentabilidad esperada',
    volatility: 'Volatilidad',
    horizon: 'Horizonte de planificación',
    jurisdiction: 'Residencia fiscal',
    risk_aversion: 'Tolerancia al riesgo',
    birth_year: 'Año de nacimiento',
    birth_date: 'Fecha de nacimiento',
    marital_status: 'Estado civil',
    dependents: 'Personas a cargo',
    dependent_ages: 'Edades de las personas a cargo',
    children: 'Hijos',
    children_birth_years: 'Años de nacimiento de los hijos',
    partner_age: 'Edad de tu pareja',
    partner_annual_income: 'Ingresos anuales de tu pareja',
    partner_retirement_age: 'Edad de jubilación de tu pareja',
    plan_to_age: 'Edad hasta la que llega el plan',
    gross_annual_income: 'Ingresos brutos anuales',
    net_income: 'Ingresos netos',
    annual_spending: 'Gasto anual',
    essential_spending: 'Gasto esencial',
    flexible_spending: 'Gasto flexible',
    investments_total: 'Total invertido',
    total_investable_wealth: 'Patrimonio invertible total',
    portfolio_mix: 'Composición de la cartera',
    cgt_rate: 'Tipo sobre ganancias de capital',
    interest_rate: 'Tipo de interés',
    dividend_yield: 'Rentabilidad por dividendo',
    wealth_tax_rate: 'Tipo del impuesto sobre el patrimonio',
    filing_jurisdictions: 'Países donde declaras',
    citizenship: 'Nacionalidad',
    tax_residency: 'Residencia fiscal declarada',
    state: 'Estado o provincia',
    region: 'Región',
    city: 'Ciudad',
    locality: 'Municipio',
    church_tax_applicable: 'Sujeto al impuesto eclesiástico',
    life_cover: 'Seguro de vida',
    income_protection: 'Seguro de rentas',
    employer_match: 'Aportación de la empresa',
    pension: 'Pensión',
    state_pension: 'Pensión pública',
    social_security: 'Seguridad Social',
    wage_growth: 'Crecimiento salarial',
    stability: 'Estabilidad de los ingresos',
    has_will: 'Testamento otorgado',
    risk_attitude: 'Actitud ante el riesgo',
  },
  keyLabels: {
    goal_priority: 'Orden de los objetivos',
    current_age: 'Edad',
    inflation: 'Hipótesis de inflación',
    jurisdiction: 'Residencia fiscal',
    realization_tax_rate: 'Impuesto al vender para financiar un objetivo',
    citizenship: 'Nacionalidad',
    total_investable: 'Patrimonio invertible',
    annual_income: 'Ingresos anuales',
    annual_savings: 'Ahorro anual',
    risk_tier: 'Nivel de riesgo',
    retirement_age: 'Edad de jubilación',
    target_wealth: 'Patrimonio objetivo',
    primary_residence_sellable: 'Venta de la vivienda / cambio a una más pequeña',
  },
  channelLabels: {
    web_levers: 'Palancas en la web',
    web_cockpit: 'Panel de decisiones',
    web: 'Sitio web',
    chat: 'Asistente de IA',
    mcp: 'Asistente de IA',
    full_review: 'Revisión completa',
    full_plan: 'Plan completo',
    what_if: 'Simulación de escenarios',
  },
  objectiveLabels: {
    status_quo: 'Situación actual',
    max_goal_probability: 'Máxima probabilidad de éxito',
    lowest_drawdown: 'Menor caída máxima',
    highest_growth: 'Mayor crecimiento',
    fastest_to_goal: 'Objetivo alcanzado antes',
  },
  choices: {
    interest_only: 'Solo intereses',
    fixed_rate_amortizing: 'Amortización (tipo fijo)',
    variable_rate_amortizing: 'Amortización (tipo variable)',
    repayment: 'Amortización',
    refinance: 'Refinanciar a tipo de mercado',
    repay: 'Amortizar por completo',
  },
  values: {
    yearsShort: (n) => `${n} años`,
    shareAbout: (pct) => `alrededor del ${pct} de su valor`,
    sharePart: 'parte de su valor',
    aroundAge: (age) => ` hacia los ${age} años`,
    inAboutYears: (n) => ` dentro de unos ${n} años`,
    maySellFor: (share, when) => `Puede venderse para liberar ${share}${when}, solo si eso mejora el plan.`,
    maySell: 'Puede venderse si eso mejora el plan.',
    structuredFact: 'Dato estructurado',
  },
  ask: { talkThrough: 'Hablarlo con MyCIO' },
  review: {
    openInputCheckFailed: 'No se pudo completar la comprobación de datos pendientes. Consulta el estado de la revisión más abajo.',
    finishedLoading: 'Tu revisión ha terminado. Cargando los resultados…',
    draftReady: 'Tu revisión provisional está lista: más abajo encontrarás el análisis completado, lo que no se pudo calcular y las preguntas que permitirían completarlo.',
    finishedNotLoaded: 'Tu revisión ha terminado, pero aún no se han podido cargar los resultados. Reintentando; el cálculo no se repetirá.',
    fetchFailed: 'Tu revisión ha terminado, pero no hemos podido recuperarla ahora mismo. Está guardada: vuelve a intentarlo en un momento; no es necesario recalcularla.',
    notComputed: (list) => `No calculado en esta ejecución: ${list}.`,
    changesNotSaved: (n, f) => `${n === 1 ? '1 cambio no se guardó' : `${f} cambios no se guardaron`}; consulta Tu información.`,
  },
  facts: {
    chooseOption: 'Elige una opción.',
    couldNotSave: 'No se pudo guardar ese valor. Tu plan no ha cambiado.',
    couldNotUndo: 'No se pudo deshacer ese cambio.',
    choosePlaceholder: 'Elegir…',
    confirmedBadge: 'Confirmado: pide el cambio en el chat',
    confirmedTitle: (was) => `Ya confirmaste este dato como ${was}. Pide a MyCIO en el chat que lo sustituya.`,
  },
  assumptionsToConfirm: { anInput: 'un dato', assumedSuffix: (v) => ` — supuesto: ${v}` },
  editor: {
    enterNumber: 'Introduce un número.',
    enterNumberFor: (l) => `Introduce un número para ${l}.`,
    enterPercentFor: (l) => `Introduce un porcentaje para ${l}.`,
    chooseYesNoFor: (l) => `Elige Sí o No para ${l}.`,
    semicolonBetween: (l) => `Separa con ";" los elementos de ${l}.`,
    useNumbersFor: (l) => `Usa números para ${l}.`,
    wholeNumbersFor: (l) => `Usa números enteros para ${l}, separados con ";".`,
    commaSeparated: 'separados por comas',
  },
  freshnessUnknown: 'No hemos podido verificar si tu cálculo guardado incluye los últimos cambios en tus cuentas. Actualiza la página y revisa tus cuentas antes de basarte en estas cifras.',
  goals: { goalN: (n) => `Objetivo ${n}`, whatMoves: 'qué lo mueve →', earliest: (y) => `lo antes posible ≈ ${y}`, rank: 'Prioridad' },
  issues: {
    mainConstraint: 'La principal restricción de este plan',
    riskToReview: 'Riesgo a revisar',
    missingInformation: 'Información pendiente',
    whyItMatters: (t) => `Por qué importa: ${t}`,
    seeEvidence: 'Ver la evidencia ↓',
    noneFound: 'El análisis completado no ha identificado ninguna cuestión relevante con la información que contiene actualmente este plan.',
    notAvailable: 'El análisis de cuestiones aún no está disponible. Que esta sección esté vacía no significa que no existan cuestiones.',
    discussQuestion: 'Revisa las cuestiones que ha identificado este plan. Explica su evidencia y pregúntame si algún dato de mi situación que falte podría cambiar su prioridad o su conclusión.',
    discussLabel: 'Comentar estas cuestiones',
  },
  policies: {
    optionTested: 'Opción analizada',
    computedEffect: (v) => `Efecto calculado: ${v}`,
    tradeoff: (v) => `Compensación: ${v}`,
    backToAction: 'Volver a la acción ↑',
    tradeoffsHeading: 'Compensaciones de los casos analizados',
  },
  transfer: {
    toHeirs: (m) => `+${m} para los herederos`,
    fundedPoints: (label, pts) => `${label} ${pts} puntos porcentuales`,
    noChange: ' · sin cambios en la financiación de tus objetivos',
  },
  tax: {
    rulesIntro: 'Solo se muestran las normas vinculadas a este cálculo. Cada entrada muestra los datos de la fuente que se registraron; las fechas o la información de cobertura que falten se indican como tales.',
    title: 'Tratamiento fiscal aplicado en este plan',
    countryBookPrimary: 'La proyección aplicó año a año la normativa fiscal del país correspondiente. Más abajo se enumeran las fuentes registradas expresamente como oficiales o primarias.',
    countryBookNoPrimary: 'La proyección aplicó año a año la normativa fiscal del país correspondiente. Este resultado guardado aún no incluye enlaces a fuentes primarias, así que verifica las decisiones con impacto fiscal antes de actuar.',
    simplified: 'El cálculo usó un tratamiento fiscal simplificado. Revisa las limitaciones que figuran más abajo antes de basarte en comparaciones con impacto fiscal.',
    askQuestion: 'Explica qué leyes fiscales y qué niveles nacionales se aplicaron a este plan, cuáles se aproximaron o se excluyeron y qué datos de mi situación fiscal podrían cambiar el resultado. Usa solo las fuentes primarias vinculadas realmente a este cálculo e indica las que falten.',
    askLabel: 'Preguntar por el tratamiento fiscal',
  },
  inputs: { title: 'Datos que podrían cambiar el resultado', infoToConfirm: 'Información por confirmar' },
  gated: {
    countChoices: (n, f) => (n === 1 ? `${f} opción analizada para ti` : `${f} opciones analizadas para ti`),
    mappedYears: (_n, f) => `hemos proyectado los próximos ${f} años`,
    extraChecks: (n, f) => (n === 1 ? `${f} comprobación adicional de tu plan` : `${f} comprobaciones adicionales de tu plan`),
    placeholderRows: ['Fila ilustrativa', 'Cifra de muestra', 'Opción de ejemplo', 'Marcador'],
  },
}

export default es
